// ═══════════════════════════════════════════
// Models matching C# DTOs exactly
// ═══════════════════════════════════════════

// ─── ContactType enum ───
export type ContactType =
  | 'Work'
  | 'Client'
  | 'Employee'
  | 'Vendor'
  | 'Consultant'
  | 'Legal'
  | 'Bank'
  | 'Government'
  | 'Family Member'
  | 'General';

// ─── PhonebookContactVM ───
export interface PhonebookContactVM {
  id: number;
  firstName: string;
  lastName: string | null;
  email: string | null;
  mobilePhone: string | null;
  workPhone: string | null;
  company: string | null;
  contactType: ContactType;
  jobTitle: string | null;
  department: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  tic: string | null;
  vatNumber: string | null;
  notes: string | null;
  preferredContactMethods: string[] | null;
  // ─── Lead capture ───
  leadRating: number | null;
  industry: string | null;
  ownerAzureObjectId: string | null;
  ownerName: string | null;
  introducerId: number | null;
  introducerName: string | null;
  linkedInUrl: string | null;
  website: string | null;
  facebook: string | null;
  instagram: string | null;
  createdAt: string;      // DateTimeOffset
  updatedAt: string | null; // DateTimeOffset?
  // Frontend-only: true when this row was synthesized from an aggregated
  // submitted-form contact (Business / Individual / Introducer) and is
  // therefore read-only (no edit / delete).
  isFormContact?: boolean;
  formContactType?: 'Business' | 'Individual' | 'Introducer';
}

// ─── Shared Lead-capture fields ───
export interface LeadFieldsDTO {
  leadRating?: number | null;
  industry?: string | null;
  ownerAzureObjectId?: string | null;
  ownerName?: string | null;
  introducerId?: number | null;
  introducerName?: string | null;
  linkedInUrl?: string | null;
  website?: string | null;
  facebook?: string | null;
  instagram?: string | null;
}

// ─── CreatePhonebookContactDTO ───
export interface CreatePhonebookContactDTO extends LeadFieldsDTO {
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  mobilePhone?: string | null;
  workPhone?: string | null;
  company?: string | null;
  contactType: ContactType;
  jobTitle?: string | null;
  department?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  tic?: string | null;
  vatNumber?: string | null;
  notes?: string | null;
  preferredContactMethods?: string[] | null;
}

// ─── UpdatePhonebookContactDTO ───
export interface UpdatePhonebookContactDTO extends LeadFieldsDTO {
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  mobilePhone?: string | null;
  workPhone?: string | null;
  company?: string | null;
  contactType: ContactType;
  jobTitle?: string | null;
  department?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  tic?: string | null;
  vatNumber?: string | null;
  notes?: string | null;
  preferredContactMethods?: string[] | null;
}

// ─── Business-card scan result (matches backend ScanCardResultDTO) ───
export interface ScanCardResult {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  mobilePhone: string | null;
  workPhone: string | null;
  company: string | null;
  jobTitle: string | null;
  department: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  website: string | null;
  linkedInUrl: string | null;
  confidence: number;
  warnings: string[];
}

// ─── Introducer option (subset of IBusiness) ───
export interface IntroducerOption {
  id: number;
  name: string;
}

// ─── Timestamped notes thread ───
export interface PhonebookContactNoteVM {
  id: number;
  phonebookContactId: number;
  body: string;
  authorAzureObjectId: string | null;
  authorName: string | null;
  createdAt: string;        // DateTimeOffset
  updatedAt: string | null; // DateTimeOffset?
}

export interface CreatePhonebookContactNoteDTO {
  body: string;
}

// ─── PhonebookListResponseVM ───
export interface PhonebookListResponseVM {
  total: number;
  items: PhonebookContactVM[];
}

// ─── PhonebookCountsVM ───
export interface PhonebookCountsVM {
  work: number;
  client: number;
  employee: number;
  vendor: number;
  consultant: number;
  legal: number;
  bank: number;
  government: number;
  familyMember: number;
  general: number;
  total: number;
}

// ─── List Query Params ───
export interface PhonebookListParams {
  search?: string;
  type?: ContactType | '';
  page?: number;
  pageSize?: number;
  sortBy?: 'firstName' | 'lastName' | 'company' | 'contactType' | 'email' | 'createdAt';
  sortDirection?: 'asc' | 'desc';
}

// ─── Dialog Data Interfaces ───
export interface ContactFormDialogData {
  contact?: PhonebookContactVM;
  mode: 'create' | 'edit';
}

export interface DeleteConfirmDialogData {
  contact: PhonebookContactVM;
}

// ─── Filter Option ───
export interface FilterOption {
  key: string;
  label: string;
  count?: number;
}
